import { Campaign, CampaignRanking } from '@/domain/types/campaigns';
import { Link, useNavigate } from 'react-router-dom';
import { MdMessage, MdPerson } from 'react-icons/md';
import { TopMentionedPublication } from '../hooks/use-dashboard';

interface Props {
  campaigns: Campaign[];
  campaignRankings: Record<string, CampaignRanking[]>;
  campaignTopPublications: Record<string, TopMentionedPublication[]>;
}

const formatName = (raw: string): string =>
  raw
    .split(' ')
    .filter((w) => w && w.toLowerCase() !== 'undefined')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ') || '—';

const CampaignRankingCard = ({ campaigns, campaignRankings, campaignTopPublications }: Props) => {
  const navigate = useNavigate();

  if (campaigns.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col gap-4">
      <span className="text-sm font-medium text-gray-500">Campañas activas</span>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {campaigns.map((c) => {
          const rankings = campaignRankings[c.id] ?? [];
          const topPubs = campaignTopPublications[c.id] ?? [];

          return (
            <div
              key={c.id}
              className="bg-gray-50 rounded-xl border border-gray-200 p-4 flex flex-col gap-3"
            >
              {/* Campaign header */}
              <div
                className="flex justify-between items-start cursor-pointer"
                onClick={() => navigate(`/dashboard/campaigns/detail/${c.id}`)}
              >
                <div>
                  <span className="text-xs font-bold text-blue-600 uppercase tracking-wide">
                    {c.hashtag}
                  </span>
                  <p className="text-sm font-semibold text-gray-800 mt-0.5 line-clamp-2">{c.name}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Hasta {c.endDate.toDate().toLocaleDateString('es-CO')}
                  </p>
                </div>
                <span className="text-xs text-blue-500 hover:underline shrink-0 ml-2">Ver más →</span>
              </div>

              {/* Top 5 users */}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                  <MdPerson size={13} /> Top usuarios por menciones
                </p>
                {rankings.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">Sin menciones aún</p>
                ) : (
                  <div className="flex flex-col gap-1">
                    {rankings.map((r, i) => (
                      <div key={r.userId} className="flex items-center justify-between py-0.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs text-gray-400 w-4 shrink-0">#{i + 1}</span>
                          {r.userPhoto ? (
                            <img
                              src={r.userPhoto}
                              alt=""
                              className="w-6 h-6 rounded-full object-cover shrink-0"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                              <MdPerson size={12} className="text-gray-400" />
                            </div>
                          )}
                          <span className="text-xs text-gray-700 truncate">{formatName(r.name)}</span>
                        </div>
                        <span className="text-xs font-bold text-blue-600 shrink-0 ml-2">
                          {r.mentionCount}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Top 3 publications mentioning this campaign's hashtag */}
              <div className="border-t border-gray-200 pt-3">
                <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                  <MdMessage size={13} /> Publicaciones con más menciones
                </p>
                {topPubs.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">Sin publicaciones aún</p>
                ) : (
                  <div className="flex flex-col gap-1">
                    {topPubs.map((pub, i) => (
                      <Link
                        key={pub.id}
                        to={pub.detailPath}
                        className="flex items-center justify-between rounded-lg hover:bg-gray-100 py-1 px-1.5 transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs text-gray-400 shrink-0">#{i + 1}</span>
                          <span className="text-xs text-gray-800 truncate">{pub.title}</span>
                        </div>
                        <div className="flex items-center gap-1 text-blue-600 shrink-0 ml-2">
                          <MdMessage size={12} />
                          <span className="text-xs font-bold">{pub.mentionCount}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CampaignRankingCard;
